import { useState, useRef, useEffect } from 'react';
import { PlaceDto } from '@/api/place';
import api from '@/api';

interface UsePlaceSearchProps {
  debounceTime?: number;
  onSearchStart?: () => void;
  onSearchComplete?: (places: PlaceDto[]) => void;
  onSearchError?: (error: any) => void;
}

interface UsePlaceSearchReturn {
  searchValue: string;
  setSearchValue: (value: string) => void;
  searchPlaces: PlaceDto[];
  isSearching: boolean;
  clearSearch: () => void;
}

/**
 * Custom hook to handle place search with debounce
 */
export const usePlaceSearch = ({
  debounceTime = 500,
  onSearchStart,
  onSearchComplete,
  onSearchError,
}: UsePlaceSearchProps = {}): UsePlaceSearchReturn => {
  const [searchValue, setSearchValue] = useState('');
  const [searchPlaces, setSearchPlaces] = useState<PlaceDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear search results and value
  const clearSearch = () => {
    setSearchValue('');
    setSearchPlaces([]);
  };

  // Handle search input change with debounce
  useEffect(() => {
    // Clear previous timeout if it exists
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout to call API after debounce time
    if (searchValue) {
      setIsSearching(true);
      if (onSearchStart) onSearchStart();

      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const places = await api.place.search({
            name: searchValue.trim(),
          });
          setSearchPlaces(places.list);
          setIsSearching(false);
          if (onSearchComplete) onSearchComplete(places.list);
        } catch (error) {
          console.error('Error searching places:', error);
          setIsSearching(false);
          if (onSearchError) onSearchError(error);
        }
      }, debounceTime);
    } else {
      searchTimeoutRef.current = setTimeout(() => {
        setSearchPlaces([]);
        setIsSearching(false);
      });
    }

    // Cleanup function to clear timeout when component unmounts or searchValue changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue, debounceTime, onSearchStart, onSearchComplete, onSearchError]);

  return {
    searchValue,
    setSearchValue,
    searchPlaces,
    isSearching,
    clearSearch,
  };
};